import { ISearchService } from "../interfaces/ISearchService"
import { FetchService } from "@/lib/http/FetchService"
import { Result } from "@/types/generic/Result"
import { SearchRequestV3 } from "@/lib/depict-client/types/api/SearchRequestV3"
import { SearchResponse } from "@/lib/depict-client/types/api/SearchResponse"
import { SearchSuggestionsRequestV3 } from "@/lib/depict-client/types/api/SearchSuggestionsRequestV3"
import { SearchSuggestionsResponseV3 } from "@/lib/depict-client/types/api/SearchSuggestionsResponseV3"
import { BaseSearchRequestV3 } from "@/lib/depict-client/types/api/BaseSearchRequestV3"
import qs from "qs"
import { ClientFetchService } from "@/lib/http/ClientFetchService"
import { RecommendResponseV3 } from "@/lib/depict-client/types/api/RecommendResponseV3"

export class SearchApiService implements ISearchService {
    constructor(private fetchService: FetchService | ClientFetchService) {}

    /**
     * Returns products and content that matches a given query, optionally filtered.
     * @param {SearchRequestV3} req
     * @link https://docs.depict.ai/reference/openapi-reference/search-v3/get-results
     */
    public getResults = async (req: SearchRequestV3): Promise<Result<SearchResponse>> => {
        return this.fetchService.fetch("/search/results", {
            method: "POST",
            body: JSON.stringify(req)
        })
    }

    /**
     * Returns suggestions for a given query. Call this while the user is typing to help the user find what they're looking for.
     * The suggestions are of two types:
     * - suggestions for search queries that are related to the current query.
     * - suggestions for listings that are related to the current query.
     * @param {SearchSuggestionsRequestV3} req
     * @link https://docs.depict.ai/reference/openapi-reference/search-v3/get-suggestions
     */
    public getSuggestions = async (
        req: SearchSuggestionsRequestV3
    ): Promise<Result<SearchSuggestionsResponseV3>> => {
        return this.fetchService.fetch("/search/suggestions?" + qs.stringify(req), {
            method: "GET"
        })
    }

    /**
     * Returns recommendations for products related to the search query.
     * It should be called once the user has exhausted the search results for their query and the recommended products are typically shown below the search results.
     * @param {BaseSearchRequestV3} req
     * @link https://docs.depict.ai/reference/openapi-reference/search-v3/get-related-recommendations
     */
    public getRelatedRecommendations = async (
        req: BaseSearchRequestV3
    ): Promise<Result<RecommendResponseV3>> => {
        return this.fetchService.fetch("/search/related", {
            method: "POST",
            body: JSON.stringify(req)
        })
    }
}
